import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import SignOffBar from "@/components/SignOffBar";   // Batch 11 · A2
import React, { useEffect, useState, useCallback } from "react";
import { fmtDate } from "@/lib/dt";
import { Link, useSearchParams } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { useI18n } from "@/contexts/I18nContext";
import FullScreenDialog from "@/components/FullScreenDialog";
import { Button } from "@/components/ui/button";
import Ref from "@/components/Ref";
import { toast } from "sonner";
import { AlertTriangle, Trash2 } from "lucide-react";
import { asArray } from "@/lib/asArray";
import { askConfirm, askPrompt } from "@/lib/appDialog";   // Batch 15 · N4 (F-118)
import useMissingFields, { FieldMessage, missingFromError } from "@/lib/useMissingFields";   // Batch 27 · Add-on 1

export default function ProceduresPerformedDialog() {
  const colW = useResizableColumns({ storageKey: "procedures-performed-dialog", defaults: [110, 160, 160, 110, 160, 240, 130, 160, 100], mins: [72, 100, 100, 72, 100, 130, 90, 100, 60] });   // Step 11d · add-on
  const [sp, setSp] = useSearchParams();
  const openCode = sp.get("openProc") || "";
  const { currentEngId } = useEngagement();
  const { t, lang } = useI18n();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const close = () => { const n = new URLSearchParams(sp); n.delete("openProc"); setSp(n); setData(null); setError(null); };
  const load = useCallback(async () => {
    if (!openCode || !currentEngId) return;
    setError(null);
    try {
      const r = await api.get(`/engagements/${currentEngId}/procedures-performed/${openCode}`);
      setData(r.data);
    } catch (e) {
      // N1 addendum — never leave the dialog stuck on "Loading…". Stop
      // the spinner and surface the server message inline, in addition
      // to the toast, so the auditor knows what to do next.
      const msg = e?.response?.data?.detail || e?.message || t("common.failed");
      setError(String(msg));
      setData({ rows: [], totals: { total: 0, answered: 0, pct: 0 },
                 decision: null, guard: null, conclusion: "",
                 preparer_name: "", reviewer_name: "", can_complete: false });
      toast.error(msg);
    }
  }, [openCode, currentEngId, t]);
  useEffect(() => { load(); }, [load]);
  const mf = useMissingFields(lang);     // Batch 27 · Add-on 1
  if (!openCode) return null;

  const answer = async (ref, body) => {
    try {
      await api.patch(`/engagements/${currentEngId}/procedures-performed/${openCode}/rows/${encodeURIComponent(ref)}`, body);
      mf.clear(`reason.${ref}`); mf.clear(`comment.${ref}`);
      load();
    } catch (e) {
      // the row's own field: "reason.<ref>" / "comment.<ref>" (the server names "reason" / "comment")
      const list = missingFromError(e).map((m) => ({ ...m, field: `${m.field}.${ref}` }));
      if (!mf.apply({ missing_fields: list })) toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const addAdditional = async () => {
    const text = await askPrompt(t("proc.newProcedure"));
    if (!text) return;
    await api.post(`/engagements/${currentEngId}/procedures-performed/${openCode}/additional`, { text_en: text, text_ar: text, assertions: [] });
    load();
  };
  const addAlternate = async (origRef) => {
    const text = await askPrompt(t("proc.newProcedure"));
    if (!text) return;
    await api.post(`/engagements/${currentEngId}/procedures-performed/${openCode}/alternate`, { original_ref: origRef, text_en: text, text_ar: text, assertions: [] });
    load();
  };
  const signoff = async (body) => {
    try {
      await api.patch(`/engagements/${currentEngId}/procedures-performed/${openCode}/signoff`, body);
      if (body.completed) mf.reset();
      if (body.conclusion !== undefined) mf.filled("conclusion", body.conclusion);
      load();
    } catch (e) { if (!mf.apply(e)) toast.error(toErrMsg(e, t("common.failed"))); }
  };
  const rows = data?.rows || [];

  return (
    <FullScreenDialog open={true} onClose={close} testid="procedures-dialog"
                      title={<span data-testid={`proc-title-${openCode}`}>
            <Ref>{openCode}-01</Ref> {t("proc.title")}
            {data && <span className="ms-2 text-xs text-muted-foreground" data-testid="proc-pct">
              {t("proc.completePct")}: {data.totals.pct}% ({data.totals.answered}/{data.totals.total})
            </span>}
      </span>}>
        <div className="pe-1">
          {data && <SignOffBar itemType="procedures" itemKey={openCode} refreshKey={data.updated_at} onChange={load} missing={mf.missing} />}
          {!data ? <div data-testid="proc-loading">{t("common.loading")}</div> : (
            <>
              {error && (
                <div className="mb-2 flex items-start gap-2 rounded border border-red-300 bg-red-50 px-3 py-2 text-[11px] text-red-800 dark:border-red-900 dark:bg-red-950/30 dark:text-red-300"
                     data-testid="proc-error">
                  <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
                  <div>{error}</div>
                </div>
              )}
              {data.guard && (
                <div className="mb-2 flex items-start gap-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-[11px] text-amber-900 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200"
                     data-testid="proc-guard">
                  <AlertTriangle className="mt-0.5 h-3 w-3 flex-none" />
                  <div>
                    {t(data.guard.key)}{" "}
                    <Link to={data.guard.link_to} onClick={close}
                          className="underline font-medium"
                          data-testid="proc-guard-link">
                      {t(data.guard.link_key)}
                    </Link>
                  </div>
                </div>
              )}
              <FieldMessage mf={mf} field="rows" testid="proc-rows-missing" />
              <ResizableTable cols={colW} wrap {...mf.props("rows", "w-full text-[11px]")}>
                <thead className="border-b bg-slate-50 dark:bg-slate-900/40 sticky top-0">
                  <tr>
                    <ResizableTh index={0} className="px-2 py-1 text-start">{t("proc.col.ref")}</ResizableTh>
                    <ResizableTh index={1} className="px-2 py-1 text-start">{t("proc.col.proc")}</ResizableTh>
                    <ResizableTh index={2} className="px-2 py-1 text-start">{t("proc.col.assertions")}</ResizableTh>
                    <ResizableTh index={3} className="px-2 py-1 text-start">{t("proc.col.source")}</ResizableTh>
                    <ResizableTh index={4} className="px-2 py-1 text-start">{t("proc.col.performed")}</ResizableTh>
                    <ResizableTh index={5} className="px-2 py-1 text-start">{t("proc.col.reason")}</ResizableTh>
                    <ResizableTh index={6} className="px-2 py-1 text-start">{t("proc.col.doneBy")}</ResizableTh>
                    <ResizableTh index={7} className="px-2 py-1 text-start">{t("proc.col.wp")}</ResizableTh>
                    <ResizableTh index={8}></ResizableTh>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <tr key={r.ref} data-testid={`proc-row-${r.ref}`} className={r.replaced_by_ref ? "opacity-60" : ""}>
                      <td className="px-2 py-1 font-mono"><Ref>{r.ref}</Ref></td>
                      <td className="px-2 py-1">{lang === "ar" ? r.text_ar : r.text_en}</td>
                      <td className="px-2 py-1">{asArray(r.assertions).join(",")}</td>
                      <td className="px-2 py-1"><span className="rounded bg-slate-100 px-1 text-[9px] dark:bg-slate-800" data-testid={`proc-source-${r.ref}`}>{t(`proc.source.${r.source}`)}</span></td>
                      <td className="px-2 py-1">
                        <select value={r.performed || ""} onChange={async (e) => {
                                  const v = e.target.value || null;
                                  if (v === "na") { const reason = await askPrompt(t("proc.naReason")); if (!reason) return; answer(r.ref, { performed: v, reason }); }
                                  else if (v === "no") { const c = await askPrompt(t("proc.noReason")); if (!c) return; answer(r.ref, { performed: v, comment: c }); }
                                  else if (v === "yes") answer(r.ref, { performed: v });
                                }}
                                disabled={!!r.replaced_by_ref}
                                data-testid={`proc-perf-${r.ref}`}
                                {...mf.props(`reason.${r.ref}`, `rounded border px-1 py-0.5 text-[10px] ${(!r.performed && mf.has("rows")) || mf.has(`comment.${r.ref}`) ? "border-destructive ring-1 ring-destructive" : ""}`)}>
                          <option value=""></option>
                          <option value="yes">{t("proc.yes")}</option>
                          <option value="no">{t("proc.no")}</option>
                          <option value="na">{t("proc.na")}</option>
                        </select>
                      </td>
                      <td className="px-2 py-1 text-[10px]">
                        {r.reason || r.comment || ""}
                        <FieldMessage mf={mf} field={`reason.${r.ref}`} testid={`proc-reason-missing-${r.ref}`} />
                        <FieldMessage mf={mf} field={`comment.${r.ref}`} testid={`proc-comment-missing-${r.ref}`} />
                        {r.replaced_by_ref && (
                          <span className="ms-1 rounded bg-amber-100 px-1 dark:bg-amber-950/40" data-testid={`proc-replaced-${r.ref}`}>
                            {t("proc.replaced")} → <Ref>{r.replaced_by_ref}</Ref>
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-1 text-[10px]">{r.done_by_name || ""} {r.done_at ? fmtDate(r.done_at) : ""}</td>
                      <td className="px-2 py-1"><input type="text" defaultValue={r.wp_ref || ""}
                          onBlur={(e) => e.target.value !== (r.wp_ref || "") && answer(r.ref, { performed: r.performed, wp_ref: e.target.value })}
                          className="w-16 rounded border px-1 py-0.5 text-[10px] dark:bg-slate-900"
                          data-testid={`proc-wp-${r.ref}`} /></td>
                      <td className="px-2 py-1"><button className="text-[10px] text-blue-700"
                          onClick={() => addAlternate(r.ref)}
                          disabled={!!r.replaced_by_ref}
                          data-testid={`proc-alt-${r.ref}`}>{t("proc.addAlternate")}</button>
                        <button className="ms-2 text-[10px] text-rose-700" data-testid={`proc-del-${r.ref}`}
                          title={t("headFolders.del.btn")}
                          onClick={async () => {
                            if (!await askConfirm(t("headFolders.del.body").replace("{name}", `${r.ref} · ${r.text_en || r.text || ""}`))) return;
                            try { await api.post(`/engagements/${currentEngId}/items/procedure_row/${r.ref}/delete`); toast.success(t("headFolders.del.done")); load(); }
                            catch (e) { toast.error(toErrMsg(e, "Failed")); }
                          }}><Trash2 className="inline h-3 w-3" /></button></td>
                    </tr>
                  ))}
                </tbody>
              </ResizableTable>
              <div className="mt-2">
                <Button size="sm" variant="outline" onClick={addAdditional} data-testid="proc-add-additional">{t("proc.addAdditional")}</Button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 border-t pt-3">
                <div><label className="block text-xs font-medium">{t("proc.conclusion")}</label>
                  <textarea rows={2} defaultValue={data.conclusion || ""}
                    onBlur={(e) => e.target.value !== (data.conclusion || "") && signoff({ conclusion: e.target.value })}
                    onChange={(e) => mf.filled("conclusion", e.target.value)}
                    data-testid="proc-conclusion"
                    {...mf.props("conclusion", "mt-1 w-full rounded border px-2 py-1 text-xs dark:bg-slate-900")} />
                  <FieldMessage mf={mf} field="conclusion" testid="proc-conclusion-missing" /></div>
              </div>
              <div className="mt-2">
                {/* Batch 27 · Add-on 1: always clickable until completed — a refusal marks exactly what is missing */}
                <Button size="sm" disabled={!!data.completed} variant={data.can_complete ? "default" : "outline"}
                        onClick={() => signoff({ completed: true })}
                        data-testid="proc-mark-complete">
                  {t("proc.markComplete")}
                </Button>
              </div>
            </>
          )}
        </div>
    </FullScreenDialog>
  );
}
